import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../utils/api";
import { Play, Eye, Clock, CheckCircle, XCircle, TrendingUp } from "lucide-react";

export default function UserDashboard() {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/reconciliation/runs")
      .then(r => setRuns(r.data))
      .finally(() => setLoading(false));
  }, []);

  const statusBadge = (status) => {
    if (status === "completed") return <span className="badge-matched">Completed</span>;
    if (status === "running") return <span className="badge-warning">Running</span>;
    if (status === "failed") return <span className="badge-unmatched">Failed</span>;
    return <span className="badge-partial">Pending</span>;
  };

  const completed = runs.filter(r => r.status === "completed");
  const avgMatch = completed.length > 0
    ? Math.round(completed.reduce((a, r) => a + (r.summary?.match_rate || 0), 0) / completed.length)
    : 0;

  return (
    <Layout>
      <div className="animate-in">
        <div style={{ marginBottom: "2rem", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <h1 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1.75rem", color: "var(--text-primary)", margin: 0 }}>My Reconciliations</h1>
            <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", marginTop: "0.25rem" }}>Upload files and run rule-based reconciliation</p>
          </div>
          <button
            data-testid="new-recon-btn"
            className="btn-primary"
            onClick={() => navigate("/reconcile")}
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            <Play size={16} strokeWidth={1.5} />
            New Reconciliation
          </button>
        </div>

        {/* Stats */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
          {[
            { label: "Total Runs", value: runs.length, icon: Clock, color: "var(--sage)" },
            { label: "Completed", value: completed.length, icon: CheckCircle, color: "#065F46" },
            { label: "Failed", value: runs.filter(r => r.status === "failed").length, icon: XCircle, color: "#DC2626" },
            { label: "Avg Match Rate", value: `${avgMatch}%`, icon: TrendingUp, color: "var(--sage)" },
          ].map(s => (
            <div key={s.label} className="recon-card" style={{ padding: "1.25rem", display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ width: "2.5rem", height: "2.5rem", borderRadius: "0.5rem", background: "var(--sage-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <s.icon size={18} style={{ color: s.color }} strokeWidth={1.5} />
              </div>
              <div>
                <p style={{ fontSize: "1.5rem", fontWeight: 700, fontFamily: "Bricolage Grotesque, sans-serif", color: "var(--text-primary)", margin: 0 }}>{s.value}</p>
                <p style={{ fontSize: "0.8rem", color: "var(--text-muted)", margin: 0, marginTop: "0.25rem" }}>{s.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Runs */}
        <div className="recon-card" style={{ padding: 0, overflow: "hidden" }}>
          <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid var(--border-default)" }}>
            <h3 style={{ fontFamily: "Bricolage Grotesque, sans-serif", fontWeight: 600, fontSize: "1rem", color: "var(--text-primary)", margin: 0 }}>Recent Runs</h3>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Name", "Rule Set", "File A", "File B", "Matched", "Match Rate", "Status", "Date", ""].map(h => (
                  <th key={h} className="table-header" style={{ textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {runs.map(r => (
                <tr key={r.id} className="table-row">
                  <td className="table-cell" style={{ fontWeight: 500 }}>{r.name}</td>
                  <td className="table-cell" style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>{r.rule_set_name}</td>
                  <td className="table-cell" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{r.file_a_name}</td>
                  <td className="table-cell" style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>{r.file_b_name}</td>
                  <td className="table-cell" style={{ fontFamily: "JetBrains Mono, monospace", fontSize: "0.8rem" }}>
                    {r.summary ? `${r.summary.matched} / ${r.summary.total_a}` : "—"}
                  </td>
                  <td className="table-cell">
                    {r.summary ? (
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <div style={{ width: "4rem", height: "4px", background: "var(--bg-subtle)", borderRadius: "2px", overflow: "hidden" }}>
                          <div style={{ height: "100%", width: `${r.summary.match_rate}%`, background: "var(--sage)", borderRadius: "2px" }} />
                        </div>
                        <span style={{ fontSize: "0.8rem", fontWeight: 600, color: "var(--text-secondary)" }}>{r.summary.match_rate}%</span>
                      </div>
                    ) : "—"}
                  </td>
                  <td className="table-cell">{statusBadge(r.status)}</td>
                  <td className="table-cell" style={{ color: "var(--text-muted)", fontSize: "0.8rem" }}>
                    {new Date(r.created_at).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    {r.status === "completed" && (
                      <button data-testid={`view-${r.id}`} onClick={() => navigate(`/results/${r.id}`)}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "var(--sage)", display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.8rem" }}>
                        <Eye size={14} /> View
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!loading && runs.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: "4rem", textAlign: "center" }}>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.75rem" }}>
                      <Play size={36} style={{ color: "var(--text-muted)" }} strokeWidth={1} />
                      <p style={{ color: "var(--text-muted)", fontSize: "0.875rem", margin: 0 }}>No reconciliation runs yet.</p>
                      <button data-testid="start-first-recon-btn" className="btn-primary" onClick={() => navigate("/reconcile")}
                        style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <Play size={14} /> Start your first reconciliation
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
